import { useSelector } from '@xstate/react';
import { ActorRefFrom, createEmptyActor } from 'xstate';
import { UserStateSnapshot } from '~/features/user/types';
import { DOCUMENTS_STATE } from '~/features/xstateSystem';
import { Provider } from '~/types';
import { UserContext } from '../../../app';
import { academicsMachine } from '../academics/academics.machine';
import { attestationMachine } from '../attestations/attestation.machine';
import { boardCertMachine } from '../board-certification/boardCert.machine';
import { certMachine } from '../certification/cert.machine';
import { cmeTrainingMachine } from '../cme-training/cmeTraining.machine';
import { CMETrainingFormValues } from '../cme-training/types';
import { documentsViewMachine } from '../documents/documents-view.machine';
import { documentsService } from '../documents/documents.service.machine';
import { employmentMachine } from '../employment/employment.machine';
import { licensesFormMachine } from '../licenses/licenseForm.machine';
import { malpracticeMachine } from '../malpractice/malpractice.machine';
import { peerRefReqMachine } from '../peer-reference-request/peerRefRequestMachine';
import { privilegesMachine } from '../privileges/privileges.machine';
import { profileFormMachine } from '../profile/profileForm.machine';
import { Context } from './types';
import { vaultState } from './vaultState.machine';

const emptyActor = createEmptyActor();

/**
 * Selects the vault ref and loading state from UserContext
 */
export const selectVaultState = (state: UserStateSnapshot) => {
  return {
    loading: state.context.loading,
    vaultRef: state.context.vaultRef,
  };
};

/**
 * Selects profile ref and provider from vault context
 * Also checks actor.children for invoked actors (like documentsView)
 */

const EMPTY_VAULT_CONTEXT = {
  documentsView: undefined,
  documentsService: undefined,
  profileRef: undefined,
  academicsRef: undefined,
  provider: undefined,
  employmentRef: undefined,
  medLicenseRef: undefined,
  licensesRef: undefined,
  boardCertRef: undefined,
  certRef: undefined,
  cmeTrainingRef: undefined,
  privilegesRef: undefined,
  malpracticeRef: undefined,
  attestationRef: undefined,
  aoiRef: undefined,
  peerReferenceRef: undefined,
  notificationsRef: undefined,
  isNotificationsOpen: false,
};

export const selectVaultContext = (state: unknown) => {
  // Handle empty actor or uninitialized state (Snapshot<undefined> doesn't have context)
  if (!state || typeof state !== 'object' || !('context' in state)) {
    return EMPTY_VAULT_CONTEXT;
  }

  // Type narrowing for MachineSnapshot with context.
  // NOTE: createEmptyActor()'s snapshot has `context` as a defined KEY but `undefined`
  // VALUE, so the `'context' in state` guard above passes. Fall back when the value
  // is actually missing to avoid TypeError on `.provider`, `.documentsService`, etc.
  const stateWithContext = state as {
    context: Context | undefined;
    children?: Record<string, import('xstate').AnyActorRef>;
  };
  if (!stateWithContext.context) return EMPTY_VAULT_CONTEXT;
  const ctx = stateWithContext.context;

  // documentsView is invoked, so it's in children, not context
  const documentsView =
    ctx.documentsView ||
    (stateWithContext.children?.[DOCUMENTS_STATE] as
      | ActorRefFrom<typeof documentsViewMachine>
      | undefined);

  return {
    documentsView: documentsView,
    documentsService: ctx.documentsService,

    profileRef: ctx.profileRef,
    academicsRef: ctx.academicsRef,
    provider: ctx.provider,
    employmentRef: ctx.employmentRef,
    medLicenseRef: ctx.medLicenseRef,
    licensesRef: ctx.licensesRef,
    boardCertRef: ctx.boardCertRef,
    certRef: ctx.certRef,
    cmeTrainingRef: ctx.cmeTrainingRef,
    privilegesRef: ctx.privilegesRef,
    malpracticeRef: ctx.malpracticeRef,
    attestationRef: ctx.attestationRef,
    aoiRef: ctx.aoiRef,
    peerReferenceRef: ctx.peerReferenceRef,
    notificationsRef: ctx.notificationsRef,
    isNotificationsOpen: ctx.isNotificationsOpen ?? false,
  };
};

type ProfileRefResult = {
  loading: boolean;
  profileRef: ActorRefFrom<typeof profileFormMachine> | undefined;
  provider: Provider | undefined;
};

export const useProfileRef = (): ProfileRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { profileRef, provider } = vaultContext;
  return { loading: loading ?? false, profileRef, provider };
};

type AcademicsRefResult = {
  loading: boolean;
  academicsRef: ActorRefFrom<typeof academicsMachine> | undefined;
  provider: Provider | undefined;
};

export const useAcademicsRef = (): AcademicsRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { academicsRef, provider } = vaultContext;
  return { loading: loading ?? false, academicsRef, provider };
};

type LicensesRefResult = {
  loading: boolean;
  licensesRef: ActorRefFrom<typeof licensesFormMachine> | undefined;
  provider: Provider | undefined;
};

export const useLicensesRef = (): LicensesRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { licensesRef, provider } = vaultContext;
  return { loading: loading ?? false, licensesRef, provider };
};

type LoadedProvider = {
  loading: boolean;
  provider: Provider | undefined;
  vaultRef: ActorRefFrom<typeof vaultState> | undefined;
};

export const useLoadedProvider = (): LoadedProvider => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { provider } = vaultContext;
  return { loading: loading ?? false, provider, vaultRef };
};

type BoardCertRefResult = {
  loading: boolean;
  boardCertRef: ActorRefFrom<typeof boardCertMachine> | undefined;
  provider: Provider | undefined;
};

export const useBoardCertRef = (): BoardCertRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { boardCertRef, provider } = vaultContext;
  return { loading: loading ?? false, boardCertRef, provider };
};

type CertificationRefResult = {
  loading: boolean;
  certRef: ActorRefFrom<typeof certMachine> | undefined;
  provider: Provider | undefined;
};

export const useCertificationRef = (): CertificationRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { certRef, provider } = vaultContext;
  return { loading: loading ?? false, certRef, provider };
};

type CMETrainingRefResult = {
  loading: boolean;
  cmeTrainingRef: ActorRefFrom<typeof cmeTrainingMachine> | undefined;
  provider: Provider | undefined;
  closeModal: () => void;
  openModal: () => void;
  addCME: (formData: Omit<CMETrainingFormValues, 'id'>) => void;
  editCME: (formData: CMETrainingFormValues) => void;
  deleteCME: (id: number) => void;
  openEditModal: (id: number) => void;
};

export const useCMETrainingRef = (): CMETrainingRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);
  if (!vaultRef) {
    return {
      loading: loading ?? false,
      cmeTrainingRef: undefined,
      provider: undefined,
      closeModal: () => null,
      openModal: () => null,
      editCME: () => null,
      addCME: () => null,
      deleteCME: () => null,
      openEditModal: () => null,
    };
  }
  const { cmeTrainingRef, provider } = useSelector(vaultRef, selectVaultContext);
  if (!cmeTrainingRef) {
    console.warn('cmeTrainingRef not found in vaultRef');
    return {
      loading: loading ?? false,
      cmeTrainingRef: undefined,
      provider: undefined,
      closeModal: () => null,
      openModal: () => null,
      editCME: () => null,
      addCME: () => null,
      deleteCME: () => null,
      openEditModal: () => null,
    };
  }
  return {
    loading: loading ?? false,
    cmeTrainingRef,
    provider,
    closeModal: () => cmeTrainingRef.send({ type: 'ON_CLOSE_MODAL' }),
    openModal: () => cmeTrainingRef.send({ type: 'ON_ADD' }),
    editCME: (formData: CMETrainingFormValues) =>
      cmeTrainingRef.send({ type: 'ON_UPDATE', values: formData }),
    deleteCME: (id: number) =>
      cmeTrainingRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } }),
    addCME: (formData: Omit<CMETrainingFormValues, 'id'>) =>
      cmeTrainingRef.send({ type: 'ON_SUBMIT', values: formData }),
    openEditModal: (id: number) => cmeTrainingRef.send({ type: 'ON_EDIT', payload: { id } }),
  };
};

type PrivilegesRefResult = {
  loading: boolean;
  privilegesRef: ActorRefFrom<typeof privilegesMachine> | undefined;
  provider: Provider | undefined;
};

export const usePrivilegesRef = (): PrivilegesRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { privilegesRef, provider } = vaultContext;
  return { loading: loading ?? false, privilegesRef, provider };
};

type MalpracticeRefResult = {
  loading: boolean;
  malpracticeRef: ActorRefFrom<typeof malpracticeMachine> | undefined;
  documentsService: ActorRefFrom<typeof documentsService> | undefined;
  provider: Provider | undefined;
};

export const useMalpracticeRef = (): MalpracticeRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { malpracticeRef, documentsService, provider } = vaultContext;
  return { loading: loading ?? false, malpracticeRef, documentsService, provider };
};

type AttestationRefResult = {
  loading: boolean;
  attestationRef: ActorRefFrom<typeof attestationMachine> | undefined;
  documentsService: ActorRefFrom<typeof documentsService> | undefined;
  provider: import('~/types').Provider | undefined;
};

// NOTE: this may want to use a documentsService instead of the documentsRef (View-Ref).
export const useAttestationRef = (): AttestationRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { attestationRef, documentsService, provider } = vaultContext;
  return { loading: loading ?? false, documentsService, attestationRef, provider };
};

type PeerReferenceRefResult = {
  loading: boolean;
  peerReferenceRef: ActorRefFrom<typeof peerRefReqMachine> | undefined;
  provider: Provider | undefined;
};

export const usePeerReferenceRef = (): PeerReferenceRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { peerReferenceRef, provider } = vaultContext;
  return { loading: loading ?? false, peerReferenceRef, provider };
};

type DocumentsRefResult = {
  loading: boolean;
  documentsView: ActorRefFrom<typeof documentsViewMachine> | undefined;
  provider: import('~/types').Provider | undefined;
};

export const useDocumentsRef = (): DocumentsRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { documentsView, provider } = vaultContext;

  return { loading: loading ?? false, documentsView, provider };
};

type DocumentsServiceResult = {
  loading: boolean;
  documentsService: ActorRefFrom<typeof documentsService> | undefined;
  provider: import('~/types').Provider | undefined;
};

export const useDocumentsService = (): DocumentsServiceResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { documentsService, provider } = vaultContext;
  return { loading: loading ?? false, documentsService, provider };
};

type EmploymentRefResult = {
  loading: boolean;
  employmentRef: ActorRefFrom<typeof employmentMachine> | undefined;
  provider: Provider | undefined;
};

export const useEmploymentRef = (): EmploymentRefResult => {
  const { loading, vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);

  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);

  const { employmentRef, provider } = vaultContext;
  return { loading: loading ?? false, employmentRef, provider };
};
